import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {  
  SignUpResp,
  SignUpVariables
} from '../../types/signUpTypes';

import type { User } from '../../types/userTypes';

const SIGN_UP = gql`
  mutation Register($email: String!,
    $password: String!, 
    $passwordConfirmation: String!, 
    $confirmUrl: String!, 
    $firstName: String!, 
    $lastName: String!
    $countryCode: String!) {
      register(email: $email, 
        password: $password, 
        passwordConfirmation: $passwordConfirmation, 
        confirmUrl: $confirmUrl,
        firstName: $firstName, 
        lastName: $lastName,
        countryCode: $countryCode
      ) {
        user {
          id
          email
          firstName
          lastName
          countryCode
        }
        errors
      }
    }
  ` 

type UseSignUpOptions = {
  onCompleted?: (user: User) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<SignUpResp, SignUpVariables>>[1];
};

const useSingUp = (options?: UseSignUpOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    SignUpResp,
    SignUpVariables
  >(SIGN_UP, options?.mutationOptions);

  const signUp = useCallback(
    async (input: SignUpVariables) => {
      try {
        const result = await mutate({
          variables: input
        });

        const register = result?.data?.register;

        if (register?.errors?.length) {
          throw new Error(register.errors.join(", "));
        }

        if (options?.onCompleted && register?.user) {
          options.onCompleted(register.user);
        }

        return register?.user;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const user = data?.register?.user;

  return {
    signUp,
    user,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useSingUp
